import { useCallback, useMemo, useEffect, useRef } from 'react';
import { debounce } from 'lodash';
import { templateSchema } from './validationSchema';
import { useAppDispatch, useAppSelector } from '../../../../store/redux/hooks';
import {
  setError,
  clearError,
  clearNamespaceErrors,
  setMultipleErrors,
} from '../../../../store/redux/slices/templateErrors.slice';
import cloneDeep from 'lodash/cloneDeep';
import { setFocusedField } from '../../../../store/redux/slices/template.slice';
import {
  FieldsState as StoreFieldsState,
  FieldSection,
} from '../../../../store/types/template';

type FieldsState = Record<string, Record<string, { id?: string } | null>>;

const hasFieldsWithData = (
  fields: FieldsState | StoreFieldsState,
  sections: string[]
): boolean => {
  return sections.some((sectionKey) => {
    const section = fields[sectionKey as FieldSection];
    return Object.values(section || {}).some(
      (field) => field !== null && field.id
    );
  });
};

export const useInitialDuplicateValidation = (
  sections: string[],
  fields: FieldsState,
  validateField: (
    field: string,
    value: unknown,
    context?: unknown,
    immediate?: boolean
  ) => void,
  skip = false
) => {
  const lastValidatedFieldsRef = useRef<string>('');

  useEffect(() => {
    if (skip) return;

    if (!hasFieldsWithData(fields, sections)) return;

    // Create a snapshot of current field IDs to detect when they actually change
    const currentFieldSnapshot = sections
      .map((sectionKey) => {
        const section = fields[sectionKey];
        return Object.entries(section || {})
          .filter(([, field]) => field !== null && field.id)
          .map(([slot, field]) => `${sectionKey}.${slot}:${field.id}`)
          .sort()
          .join('|');
      })
      .join('||');

    if (currentFieldSnapshot === lastValidatedFieldsRef.current) return;

    lastValidatedFieldsRef.current = currentFieldSnapshot;

    queueMicrotask(() => {
      sections.forEach((sectionKey) => {
        const section = fields[sectionKey];
        Object.entries(section || {}).forEach(([slot, field]) => {
          if (field !== null && field.id) {
            validateField(
              `${sectionKey}.${slot}.id`,
              field.id,
              { fields },
              true
            );
          }
        });
      });
    });
  }, [sections, fields, validateField, skip]);
};

export const useAllFieldsDuplicateValidation = () => {
  const fields = useAppSelector((s) => s.template.fields);
  const { validateField } = useTemplateFieldValidation('fields');
  const allSections: FieldSection[] = ['header', 'central', 'details', 'back'];

  useInitialDuplicateValidation(allSections, fields, validateField);
};

export const useTemplateFieldValidation = (namespace?: string) => {
  const dispatch = useAppDispatch();
  const setDeep = (obj: any, path: string, val: any) => {
    const parts = path.split('.');
    let cur = obj;
    for (let i = 0; i < parts.length - 1; i++) {
      cur[parts[i]] = cur[parts[i]] ?? {};
      cur = cur[parts[i]];
    }
    cur[parts[parts.length - 1]] = val;
    return obj;
  };

  const normalisePath = (path: string) => path.replace(/\[(\d+)\]/g, '.$1');

  const validateFieldImmediate = useCallback(
    async (field: string, value: any, ctx?: any) => {
      const path = namespace ? `${namespace}.${field}` : field;
      const root = cloneDeep(ctx || {});
      setDeep(root, path, value);

      const normalised = normalisePath(path);

      try {
        await templateSchema.validateAt(path, root, {
          context: {
            mode: 'field',
            ...ctx,
            root,
          },
        });
        dispatch(clearError(normalised));
      } catch (err: any) {
        dispatch(setError({ path: normalised, message: err.message }));
      }
    },
    [namespace, dispatch]
  );

  const debouncedValidateField = useMemo(
    () => debounce(validateFieldImmediate, 300),
    [validateFieldImmediate]
  );

  const validateSection = useCallback(
    async (context?: any, validationContext?: any) => {
      if (!namespace) return;
      dispatch(clearNamespaceErrors(namespace));
      try {
        await templateSchema.validateAt(
          namespace,
          { [namespace]: context },
          {
            abortEarly: false,
            context:
              validationContext !== undefined ? validationContext : context,
          }
        );
      } catch (err: any) {
        const sectionErrors: Record<string, string> = {};
        if (err.inner && Array.isArray(err.inner) && err.inner.length > 0) {
          err.inner.forEach((e: any) => {
            if (e.path) {
              const normalised = normalisePath(e.path);
              sectionErrors[normalised] = e.message;
            }
          });
        } else if (err.path && err.message) {
          sectionErrors[normalisePath(err.path)] = err.message;
        }
        if (Object.keys(sectionErrors).length > 0) {
          dispatch(setMultipleErrors(sectionErrors));
        }
      }
    },
    [namespace]
  );

  const validateField = useCallback(
    (field: string, value: any, context?: any, immediate = false) => {
      if (immediate) {
        validateFieldImmediate(field, value, context);
      } else {
        debouncedValidateField(field, value, context);
      }
    },
    [validateFieldImmediate, debouncedValidateField]
  );

  useEffect(() => {
    return () => {
      debouncedValidateField.cancel();
    };
  }, [debouncedValidateField]);

  const handleFocus = useCallback(
    (fieldIdentifier: string) => {
      dispatch(setFocusedField(fieldIdentifier));
    },
    [dispatch]
  );

  const handleBlur = useCallback(() => {
    dispatch(setFocusedField(null));
  }, [dispatch]);

  return { validateField, validateSection, handleFocus, handleBlur };
};
